import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import Reveal, { SectionLabel } from "@/components/Reveal";
import { Radio } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DailyFeed = () => {
  const [posts, setPosts] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const load = () =>
      axios.get(`${API}/community-feed`).then((r) => setPosts(r.data.posts || [])).catch(() => {});
    load();
    const refresh = setInterval(load, 60000);
    return () => clearInterval(refresh);
  }, []);

  useEffect(() => {
    if (posts.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % posts.length), 5000);
    return () => clearInterval(t);
  }, [posts.length]);

  const post = posts[idx];

  return (
    <section id="daily-feed" className="relative py-24 lg:py-36 bg-panel/30 border-y border-line/60" data-testid="daily-feed-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 lg:mb-16">
          <Reveal>
            <SectionLabel>Live From The Community</SectionLabel>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight" data-testid="daily-feed-title">
              Fresh proof.
              <span className="text-gold-gradient"> Every single day.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-emerald-400 uppercase" data-testid="daily-feed-live-badge">
              <Radio size={13} className="animate-pulse" /> Auto-updates from the Atlas chat
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="glass-panel gold-ring chart-frame border border-line min-h-[320px] sm:min-h-[380px] relative overflow-hidden" data-testid="daily-feed-card">
            {post ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={post.message_id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -24 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className={`grid ${post.media ? "md:grid-cols-2" : ""} h-full`}
                >
                  {post.media && (
                    <div className="relative bg-black">
                      <img
                        src={`${BACKEND}/api/feed-media/${post.media}`}
                        alt="Atlas community post"
                        loading="lazy"
                        className="w-full h-64 md:h-full object-cover object-top"
                        data-testid="daily-feed-image"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent md:bg-gradient-to-r" />
                    </div>
                  )}
                  <div className="p-7 sm:p-10 flex flex-col justify-center">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-gold/80 uppercase mb-4">
                      {new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · Atlas Community
                    </p>
                    <p className="text-base sm:text-lg text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="daily-feed-text">
                      {post.text || "Shared in the Atlas community."}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8" data-testid="daily-feed-empty">
                <p className="font-display font-bold text-xl sm:text-2xl text-slate-200">Member wins and markups land here daily.</p>
                <p className="mt-3 text-sm text-slate-500 max-w-md">
                  This wall streams straight from the Atlas community chat — new progress rotates in automatically.
                </p>
              </div>
            )}
          </div>
        </Reveal>

        {posts.length > 1 && (
          <div className="mt-6 flex justify-center gap-2" data-testid="daily-feed-dots">
            {posts.slice(0, 10).map((p, i) => (
              <button
                key={p.message_id}
                onClick={() => setIdx(i)}
                aria-label={`Show post ${i + 1}`}
                data-testid={`daily-feed-dot-${i + 1}`}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                  i === idx ? "w-8 bg-gold" : "w-3 bg-line hover:bg-slate-600"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default DailyFeed;
